/**
 * Chat message rendering components.
 *
 * Extracted verbatim from `pages/Workspace.tsx` (crucible plan 001
 * Step 5.2). This module owns the presentational chat-transcript UI:
 * message bubbles, tool-call cards + policy/pedagogical chips, the
 * AskUserQuestion form + schema-error rebound, the markdown-question
 * fallback strip, the Phase-B decision card, the shared markdown
 * renderer, and the typing indicator. All components are pure
 * presentational (local UI state only, no chat reducer / IPC brain) —
 * they receive data + callbacks as props. Behavior is byte-for-byte
 * preserved; `Workspace.tsx` re-exports the test-facing symbols.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ASK_USER_QUESTION_SCHEMA_ERROR_KEY } from '@volley/crucible/runtime/ask-user-question/answer-permission-result.js';
import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { PhaseBDecision } from '@volley/crucible-ipc-types/build-game-types';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import type { WorkspaceRunState } from '../workspace/run-state';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { ChatErrorBoundary, ChatSurfaceContext } from '../components/ChatErrorBoundary';
import { ActivityCheckpointMessage } from '../components/ActivityCheckpointMessage';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useCrucibleClient } from '../transport/context';
import { parseMarkdownQuestion } from './markdown-question';
import type { ChatMessage, MessageBlock, ToolCallEntry } from './types';
import {
  buildToolCallFriendlyLabel,
  groupNewUiToolCallsForDisplay,
  isPedagogicalToolError,
  isRecoverableToolError,
  pedagogicalToolErrorLabel,
  recoverableToolErrorLabel,
  toolSubjectText,
  toolVerbPast,
  toolVerbPresent,
} from './tool-call-classification';
import { Button } from '../components/ui/button';

// Exported for unit tests (issue #369 — pin the retry-button
// visibility gate); not part of any public API.
export function ChatBubble({
  message,
  onAnswerMarkdownQuestion,
  onRetry,
  newUi,
  pinnedAskUserQuestionId = null,
}: {
  message: ChatMessage;
  /**
   * Issue #215 — fallback parser hook. When the assistant emits a
   * plain-markdown `Question N of M:` block instead of a structured
   * `AskUserQuestion` tool call, the bubble paints a button strip
   * below the rendered markdown. Clicking a button calls this
   * handler with the option's plain text (no `(a) ` prefix), which
   * the parent dispatches as a normal user message via the same
   * path the textarea uses.
   *
   * Optional so unrelated callers (tests, future re-uses) don't
   * have to wire it up — the strip simply doesn't render when the
   * handler is absent.
   */
  onAnswerMarkdownQuestion?: (optionText: string) => void;
  /**
   * Issue #369 — chat-bubble retry. Wired through to
   * `<CrucibleErrorState onRetry>` only when the bubble's error has
   * `retryable: true` (set by PR #368 for the inFlight bouncer's
   * structured-error path). Optional so tests / future re-uses can
   * mount a bubble without a parent that knows how to retry.
   */
  onRetry?: (message: ChatMessage) => void;
  newUi?: boolean;
  pinnedAskUserQuestionId?: string | null;
}): React.JSX.Element | null {
  // Crucible #602 PR #C4 (Issue E) — inline-checkpoint discriminator
  // branch. Mounted as a single-line early-return at the top of the
  // function per design §9 ("`ChatBubble` discriminator branch").
  // Defence-in-depth: a malformed message that arrives with
  // `role === 'inline-checkpoint'` but no structured `checkpoint`
  // payload short-circuits to `null` so the renderer never crashes
  // on a corrupt row (the reducer + emitter both guard against this,
  // but a hand-edited `chat-history.json` could surface one).
  // `<ActivityCheckpointMessage>` is itself flag-gated for belt-and-
  // braces — see §9 "Why no `useActiveBuild` gating".
  if (message.role === 'inline-checkpoint') {
    if (!message.checkpoint) return null;
    return (
      <ActivityCheckpointMessage checkpoint={message.checkpoint} timestamp={message.timestamp} />
    );
  }
  const isUser = message.role === 'user';
  // Per-bubble "answered" flag: once the user clicks a button in the
  // fallback strip, every button in THIS strip disables. Keyed by the
  // bubble (the `useState` is local to this instance) so a new
  // message id mounts a fresh, un-answered bubble. Cross-game nav
  // resets messages → React unmounts these components → flag goes
  // with them. No ref to message.id needed.
  const [answeredFallback, setAnsweredFallback] = useState(false);

  // Detect the AskUserQuestion drift shape. Only fires for assistant
  // bubbles whose content has no structured tool call already (the
  // structured card path stays authoritative). Memo deps include
  // the tool-call ARRAY LENGTH rather than the array reference —
  // the reducer rebuilds `message.toolCalls` immutably on every
  // chunk, so a reference dep would defeat memoisation. Length
  // changes capture the structural transition we actually care
  // about (0 → ≥1 = structured card now exists, suppress fallback).
  const toolCallCount = message.toolCalls?.length ?? 0;
  const fallbackQuestion = useMemo(() => {
    if (isUser) return null;
    if (toolCallCount > 0) return null;
    if (!message.content) return null;
    return parseMarkdownQuestion(message.content);
  }, [isUser, toolCallCount, message.content]);

  return (
    <div className={`flex ${isUser ? 'mb-4 justify-end' : 'mb-8 justify-start'}`}>
      <div
        // Issue #328: the bubble wrapper itself MUST NOT carry
        // `select-text` / `cursor-text` — interactive descendants
        // (option buttons, tool-call cards) would become text-draggable.
        // Selectability is scoped to the message-content shim below.
        className={
          isUser
            ? 'max-w-[75%] rounded-[18px] rounded-br-md px-4 py-2.5 text-[14px] leading-[1.55]'
            : 'w-full text-[15px] leading-[1.7]'
        }
        style={
          isUser
            ? { background: 'var(--surface-chat)', color: 'var(--text-1)' }
            : { color: 'var(--text-1)' }
        }
        data-testid="chat-message-bubble"
      >
        {/* Interleaved render (Claude-Code style): when the reducer
            captured ordered `blocks`, paint text / thinking / tool cards
            in EMIT ORDER so commentary and actions thread together.
            Falls back to the legacy "all text, then all tool cards" path
            for messages without `blocks` (e.g. rehydrated from history's
            concatenated finalText). User bubbles never carry blocks. */}
        {!isUser && message.blocks && message.blocks.length > 0 ? (
          <InterleavedMessageBody
            blocks={message.blocks}
            toolCalls={message.toolCalls ?? []}
            newUi={newUi}
            pinnedAskUserQuestionId={pinnedAskUserQuestionId}
          />
        ) : (
          <>
            {message.content && (
              // Shim div: `MessageContent` returns a fragment so we can't
              // attach className directly. `select-text` makes the prose
              // copyable; `cursor-text` advertises the affordance.
              <div className="cursor-text select-text">
                <MessageContent content={message.content} />
              </div>
            )}
            {message.toolCalls && message.toolCalls.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {newUi
                  ? renderNewUiToolCallSegments(message.toolCalls, pinnedAskUserQuestionId)
                  : message.toolCalls.map((tc) =>
                      renderToolCallEntry(tc, newUi, pinnedAskUserQuestionId),
                    )}
              </div>
            )}
          </>
        )}
        {fallbackQuestion && onAnswerMarkdownQuestion && (
          <MarkdownQuestionFallbackStrip
            question={fallbackQuestion}
            answered={answeredFallback}
            onAnswer={(text) => {
              if (answeredFallback) return;
              setAnsweredFallback(true);
              onAnswerMarkdownQuestion(text);
            }}
          />
        )}
        {message.error && (
          <div className="mt-2">
            {/* Issue #369 — retry is wired ONLY when the structured
                error declares itself `retryable: true` (currently the
                inFlight bouncer per PR #368; future retryable codes
                pick this up automatically). Non-retryable errors fall
                through to the original "Copy details + Bug report"
                affordance — silently re-firing a multi-turn-state
                error would be confusing UX, so the gate stays
                explicit. The handler in `Workspace.handleRetry`
                debounces to the bouncer's 1.5s grace window. */}
            <CrucibleErrorState
              error={message.error}
              compact
              onRetry={message.error.retryable && onRetry ? () => onRetry(message) : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Render an assistant message's content as an ordered sequence of
 * text / thinking / tool-call / sub-agent segments (emit order), instead of
 * the legacy text-then-cards flattening. Contiguous text runs are already
 * coalesced into single `text` blocks by the reducer, so this maps 1:1
 * over `blocks`. Each `tool_call` block resolves its live
 * `ToolCallEntry` from `toolCalls` by id (status/output update in place
 * without disturbing the block order). Blocks attributed to a sub-agent
 * (`block.agentId`) are indented with a left rule so the user can see which
 * specialist produced them (Claude-Code parity).
 */
function InterleavedMessageBody({
  blocks,
  toolCalls,
  newUi,
  pinnedAskUserQuestionId,
}: {
  blocks: readonly MessageBlock[];
  toolCalls: readonly ToolCallEntry[];
  newUi?: boolean;
  pinnedAskUserQuestionId: string | null;
}): React.JSX.Element {
  const byId = useMemo(() => {
    const map = new Map<string, ToolCallEntry>();
    for (const tc of toolCalls) map.set(tc.id, tc);
    return map;
  }, [toolCalls]);

  // Resolve a sub-agent's display name from its most recent lifecycle marker,
  // so an attributed text/tool block can be labelled even though the chunk only
  // carries the opaque agentId.
  const agentTypeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of blocks) {
      if (b.kind === 'subagent') map.set(b.agentId, b.agentType);
    }
    return map;
  }, [blocks]);

  const renderInner = (block: MessageBlock): React.ReactNode => {
    if (block.kind === 'text') {
      if (!block.text) return null;
      return (
        <div className="cursor-text select-text">
          <MessageContent content={block.text} />
        </div>
      );
    }
    if (block.kind === 'thinking') {
      if (!block.text) return null;
      return <ThinkingBlock text={block.text} />;
    }
    if (block.kind === 'subagent') {
      return <SubagentMarker block={block} />;
    }
    // tool_call — resolve the live entry; skip if it isn't tracked
    // yet (defensive: a ref block whose entry was trimmed/never
    // landed shouldn't crash the render).
    const entry = byId.get(block.toolCallId);
    if (!entry) return null;
    return renderToolCallEntry(entry, newUi, pinnedAskUserQuestionId);
  };

  return (
    <div className="space-y-2">
      {blocks.map((block, i) => {
        const inner = renderInner(block);
        if (inner === null) return null;
        // Attribution indent: a block produced inside a sub-agent gets a left
        // rule + a small label so the transcript reads like Claude Code's
        // nested Task output. `subagent` markers carry their own styling.
        const agentId = block.kind === 'subagent' ? undefined : block.agentId;
        if (agentId !== undefined) {
          const label = agentTypeById.get(agentId) ?? 'sub-agent';
          return (
            <div
              key={`b-${i}`}
              className="border-l-2 pl-3"
              style={{ borderColor: 'var(--border-alpha-2)' }}
            >
              <div
                className="mb-1 text-[11px] font-medium uppercase tracking-wide"
                style={{ color: 'var(--text-3)' }}
              >
                {label}
              </div>
              {inner}
            </div>
          );
        }
        return <div key={`b-${i}`}>{inner}</div>;
      })}
    </div>
  );
}

/**
 * Inline sub-agent (Task) lifecycle marker — "▸ dispatched art-director" /
 * "✓ art-director finished" — threaded into the transcript at the point the
 * Task started/stopped, so the user sees specialist hand-offs in context
 * (Claude-Code parity) rather than only in the right-column Agents panel.
 */
function SubagentMarker({
  block,
}: {
  block: Extract<MessageBlock, { kind: 'subagent' }>;
}): React.JSX.Element {
  const isStart = block.phase === 'start';
  const failed = block.stopReason === 'error' || block.stopReason === 'interrupted';
  const glyph = isStart ? '▸' : failed ? '⚠' : '✓';
  const verb = isStart
    ? 'dispatched'
    : block.stopReason === 'interrupted'
      ? 'stopped'
      : block.stopReason === 'error'
        ? 'failed'
        : 'finished';
  return (
    <div
      className="flex items-center gap-1.5 text-[12px]"
      style={{ color: failed ? 'var(--text-2)' : 'var(--text-3)' }}
    >
      <span aria-hidden>{glyph}</span>
      <span>
        {isStart ? (
          <>
            Dispatched <span className="font-medium">{block.agentType}</span>
          </>
        ) : (
          <>
            <span className="font-medium">{block.agentType}</span> {verb}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Dim, collapsible reasoning block — the model's extended-thinking
 * content surfaced inline (Claude-Code style). Collapsed by default so a
 * long reasoning trace doesn't dominate the transcript; the user expands
 * it to read the commentary.
 */
function ThinkingBlock({ text }: { text: string }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="rounded-lg border px-3 py-2 text-[13px]"
      style={{ borderColor: 'var(--border-alpha-1)', color: 'var(--text-3)' }}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-start gap-1.5 text-left h-auto p-0 font-normal rounded-none"
        style={{ color: 'var(--text-3)' }}
        aria-expanded={expanded}
      >
        <span aria-hidden className="text-[11px]">
          {expanded ? '▾' : '▸'}
        </span>
        <span className="italic">Thinking</span>
      </Button>
      {expanded && (
        <div className="mt-1.5 cursor-text select-text whitespace-pre-wrap italic leading-relaxed">
          {text}
        </div>
      )}
    </div>
  );
}

function renderToolCallEntry(
  toolCall: ToolCallEntry,
  newUi?: boolean,
  pinnedAskUserQuestionId?: string | null,
): React.JSX.Element | null {
  // AskUserQuestion gets a dedicated interactive card — option buttons
  // that auto-fill + send the answer — rather than the generic row.
  if (
    newUi &&
    toolCall.name === 'AskUserQuestion' &&
    toolCall.status === 'running' &&
    toolCall.id === pinnedAskUserQuestionId
  ) {
    return null;
  }
  return toolCall.name === 'AskUserQuestion' ? (
    <AskUserQuestionCard key={toolCall.id} toolCall={toolCall} />
  ) : (
    <ToolCallCard key={toolCall.id} toolCall={toolCall} newUi={newUi} />
  );
}

function renderNewUiToolCallSegments(
  toolCalls: readonly ToolCallEntry[],
  pinnedAskUserQuestionId: string | null,
): React.ReactNode[] {
  return groupNewUiToolCallsForDisplay(toolCalls).map((segment) => {
    if (segment.kind === 'routine-group') {
      return <NewUiRoutineToolGroup key={segment.key} toolCalls={segment.toolCalls} />;
    }
    return renderToolCallEntry(segment.toolCall, true, pinnedAskUserQuestionId);
  });
}

/**
 * Issue #215 — fallback option-button strip rendered underneath an
 * assistant bubble whose markdown content matched
 * `parseMarkdownQuestion`. Visual style mirrors `AskUserQuestionCard`
 * (the structured-tool-call form) so a user can't tell the difference
 * between a model that obeyed the SKILL contract and a model that
 * drifted. Buttons disable in unison once the user clicks any one of
 * them — the parent already dispatched the answer through the same
 * path the textarea uses.
 *
 * Exported for unit tests / SSR snapshots. Not used elsewhere.
 */
export function MarkdownQuestionFallbackStrip({
  question,
  answered,
  onAnswer,
}: {
  question: { question: string; options: readonly string[] };
  answered: boolean;
  onAnswer: (optionText: string) => void;
}): React.JSX.Element {
  return (
    <div
      className="mt-2 rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3"
      data-testid="markdown-question-fallback-strip"
    >
      <p className="mb-2 text-xs italic text-white/40">Quick answer:</p>
      <div className="grid gap-1.5">
        {question.options.map((opt, oi) => (
          <Button
            key={oi}
            type="button"
            variant="secondary"
            disabled={answered}
            onClick={() => onAnswer(opt)}
            className="group flex flex-col items-start rounded-lg h-auto text-left px-3 py-2 transition-colors hover:border-crucible-accent/40 hover:bg-crucible-accent/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:bg-black/20"
          >
            <span className="text-sm font-medium text-white/90 group-hover:text-white">{opt}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

function phaseBDecisionCategoryLabel(category: PhaseBDecision['category']): string {
  switch (category) {
    case 'reviewer-block':
      return 'The plan review found a blocker. Choose how Foundry should proceed.';
    case 'reviewer-concerns-high':
      return 'The review raised high concerns. Choose how Foundry should proceed.';
    case 'install-failed':
      return 'The build install failed. Choose the recovery path.';
    case 'missing-env-var':
      return 'The build needs missing configuration. Choose the recovery path.';
    case 'destructive-confirm':
      return 'This step needs confirmation before continuing.';
    case 'build-path':
      return 'Choose the build path before continuing.';
    case 'same-finding-3-rounds':
      return 'The same finding repeated. Choose how to handle it.';
    case 'scope-ambiguity':
      return 'The build scope is ambiguous. Choose a path forward.';
  }
}

export function PendingPhaseBDecisionCard({
  decision,
  answered,
  onAnswer,
}: {
  decision: PhaseBDecision;
  answered: boolean;
  onAnswer: (optionLabel: string) => void;
}): React.JSX.Element {
  const options = decision.optionIds
    .map((id) => ({ id, label: decision.optionLabels[id] ?? id }))
    .filter((option) => option.label.trim().length > 0);

  if (answered) {
    return (
      <div className="rounded-lg border border-[var(--color-success-border)] bg-[var(--color-success-subtle)] px-3 py-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 text-xs font-medium uppercase text-emerald-300/80">Sent</span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-white/40">Decision sent</p>
            <p className="mt-0.5 text-sm font-medium text-white/90">
              Foundry will continue from your selected path.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3">
      <span className="mb-1 inline-block rounded-full bg-crucible-accent/20 px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-crucible-accent-hover">
        Need direction
      </span>
      <p className="mb-2 text-sm font-medium text-white/90">
        {phaseBDecisionCategoryLabel(decision.category)}
      </p>
      {options.length > 0 ? (
        <div className="grid gap-1.5">
          {options.map((option) => (
            <Button
              key={option.id}
              type="button"
              variant="secondary"
              onClick={() => onAnswer(option.label)}
              className="group flex flex-col items-start rounded-lg h-auto text-left px-3 py-2 transition-colors hover:border-crucible-accent/40 hover:bg-crucible-accent/10"
            >
              <span className="text-sm font-medium text-white/90 group-hover:text-white">
                {option.label}
              </span>
            </Button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-white/50">Type your answer below to continue.</p>
      )}
    </div>
  );
}

/**
 * Issue #619 \u2014 subdued info chip for pedagogical tool-result errors.
 * Symmetric with `RoutedHintChip` (PR #586) but triggered by
 * renderer-side content inspection rather than the `policyKind`
 * side-channel from the main process.
 *
 * Visual contract:
 *   - subdued background (`bg-sky-900/30` + `border-sky-700/40`)
 *     distinguishes from the `RoutedHintChip` slate tones while
 *     sharing the "non-alarming" palette.
 *   - \u2139 glyph (literal Unicode, no SVG) to signal informational.
 *   - single-line label (from `pedagogicalToolErrorLabel`) in the
 *     closed shape; click-to-expand reveals the full recovery copy.
 *
 * Intentionally NOT exported as a generic chip \u2014 the only call site
 * is `ToolCallCard`. Pure presentational component, no IPC.
 */
export function PedagogicalToolErrorChip({
  toolCall,
}: {
  toolCall: ToolCallEntry;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const output = toolCall.output ?? '';
  const label = pedagogicalToolErrorLabel(toolCall) ?? output.split('\n')[0] ?? output;
  return (
    <div
      className="rounded-lg border border-sky-700/40 bg-sky-900/30 px-3 py-2"
      data-testid="tool-call-pedagogical"
      data-pedagogical="true"
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start justify-start gap-2 text-left text-xs text-sky-200/80 transition-colors hover:text-sky-100 h-auto p-0 font-normal rounded-none"
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide recovery details' : 'Show recovery details'}
      >
        {/* Literal "i in circle" glyph \u2014 no icon dep, accessibility-safe. */}
        <span className="select-none text-sky-400/90" aria-hidden="true">
          {'\u24d8'}
        </span>
        <span className="font-mono font-medium text-sky-200/70">{toolCall.name}</span>
        <span className="flex-1 truncate text-sky-300/60">{label}</span>
        <span className="select-none text-sky-400/60" aria-hidden="true">
          {expanded ? '\u25be' : '\u25b8'}
        </span>
      </Button>
      {expanded && (
        <pre
          className="mt-2 max-h-48 cursor-text select-text overflow-auto whitespace-pre-wrap rounded border border-sky-700/30 bg-slate-800/40 px-2 py-1 text-xs text-sky-200/70"
          data-testid="tool-call-pedagogical-body"
        >
          {output}
        </pre>
      )}
    </div>
  );
}

/**
 * Issue #529 \u2014 subdued info chip for `policy_routed` denials. The
 * permission gate has nudged the agent toward a preferred shape (use
 * relative paths, dispatch a specialist via the Agent tool, use `&&`
 * instead of `;`, etc.); the agent recovers on the next call WITHOUT
 * user intervention. Pre-#529 these denials rendered as alarming red
 * error chips even though they're pedagogical, not failures \u2014 every
 * `/build-game` session exposed the user to a dozen of them.
 *
 * Visual contract:
 *   - subdued background (`bg-slate-700/40`) rather than red \u2014 does
 *     not pull the eye the way the existing red error chip does.
 *   - \u2139 icon to signal "informational, not error".
 *   - single-line summary ("Foundry routed this call \u2192 details") on
 *     the closed shape so the chat scroll stays compact.
 *   - click-to-expand body reveals the full deny reason for the
 *     curious + the agents-on-camera mode.
 *
 * Intentionally NOT exported as a generic chip \u2014 the only call site
 * is `ToolCallCard` when it detects `toolCall.policyKind === 'routed'`,
 * which is set by the chat reducer when a `policy_decision` chunk
 * lands. Pure presentational component, no IPC.
 */
export function RoutedHintChip({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // The deny reason is the gate's full prose nudging the model
  // toward the preferred shape. The model has already self-
  // recovered by the time the user reads this, so the body is
  // collapsed by default \u2014 clicking reveals the full guidance.
  const reason = toolCall.output ?? '';
  // First-line preview: trim the prose to the first sentence (up to
  // 80 chars) so the closed shape reads as a single subdued line.
  // Falls back to the full reason when no sentence boundary is in
  // range.
  const previewSource = reason.split('\n')[0] ?? reason;
  const firstSentenceEnd = previewSource.indexOf('. ');
  const preview =
    firstSentenceEnd > 0 && firstSentenceEnd <= 80
      ? previewSource.slice(0, firstSentenceEnd + 1)
      : previewSource.length > 80
        ? previewSource.slice(0, 80) + '...'
        : previewSource;
  return (
    <div
      className="rounded-lg border border-slate-600/40 bg-slate-700/30 px-3 py-2"
      data-testid="tool-call-routed"
      data-policy-kind="routed"
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start justify-start gap-2 text-left text-xs text-slate-300/80 transition-colors hover:text-slate-200 h-auto p-0 font-normal rounded-none"
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide routing details' : 'Show routing details'}
      >
        {/* Use a literal "i in circle" glyph rather than an SVG so the
            chip stays purely-text \u2014 no icon dependency, no
            accessibility-tree mismatch on a future asset swap. */}
        <span className="select-none text-slate-400/90" aria-hidden="true">
          {'\u24D8'}
        </span>
        <span className="font-mono font-medium text-slate-200/70">{toolCall.name}</span>
        <span className="flex-1 truncate text-slate-300/60">{preview}</span>
        <span className="select-none text-slate-400/60" aria-hidden="true">
          {expanded ? '\u25BE' : '\u25B8'}
        </span>
      </Button>
      {expanded && (
        <pre
          className="mt-2 max-h-48 cursor-text select-text overflow-auto whitespace-pre-wrap rounded border border-slate-600/30 bg-slate-800/40 px-2 py-1 text-xs text-slate-200/70"
          data-testid="tool-call-routed-body"
        >
          {reason}
        </pre>
      )}
    </div>
  );
}

// ─── New-UI compact tool-call row (Claude Code-style) ────────────────────────

function NewUiRoutineToolGroup({
  toolCalls,
}: {
  toolCalls: readonly ToolCallEntry[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const count = toolCalls.length;
  return (
    <div
      className="rounded-lg border border-white/5 bg-white/[0.025] px-2 py-1"
      data-testid="new-ui-routine-tool-group"
    >
      <Button
        type="button"
        variant="ghost"
        className="flex w-full items-center justify-start gap-2 h-auto py-1 text-left text-xs text-white/45 transition-colors hover:text-white/65 p-0 font-normal rounded-none"
        aria-expanded={open}
        aria-label={open ? 'Hide routine activity details' : 'Show routine activity details'}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="w-3.5 shrink-0 text-center text-white/25">✓</span>
        <span className="flex-1 truncate">
          Activity - {count} completed {count === 1 ? 'command' : 'commands'}
        </span>
        <span className="select-none text-white/25" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </Button>
      {open && (
        <div className="mt-0.5 border-t border-white/5 pt-0.5">
          {toolCalls.map((toolCall) => (
            <NewUiToolCallRow key={toolCall.id} toolCall={toolCall} />
          ))}
        </div>
      )}
    </div>
  );
}

function NewUiToolCallRow({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const isRecoverable = isRecoverableToolError(toolCall);
  const recoverableLabel = isRecoverable ? recoverableToolErrorLabel(toolCall) : null;
  const friendlyLabel = !isRecoverable ? buildToolCallFriendlyLabel(toolCall) : null;
  const subject = toolSubjectText(toolCall);
  const verb =
    toolCall.status === 'running' ? toolVerbPresent(toolCall.name) : toolVerbPast(toolCall.name);
  const hasExpandable = !!toolCall.output && toolCall.status !== 'running' && !isRecoverable;

  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        className="flex w-full items-center justify-start gap-1.5 h-auto py-[3px] text-left p-0 font-normal rounded-none"
        onClick={() => hasExpandable && setOpen((o) => !o)}
      >
        <span className="w-3.5 shrink-0 text-center text-xs">
          {toolCall.status === 'running' ? (
            <span className="tool-call-spinner inline-block text-white/40">◠</span>
          ) : toolCall.status === 'success' ? (
            <span className="text-white/25">✓</span>
          ) : isRecoverable ? (
            <span className="text-amber-400/60">○</span>
          ) : (
            <span className="text-red-400">✕</span>
          )}
        </span>
        <span className="flex-1 truncate text-xs">
          {friendlyLabel ? (
            <span className="text-white/55">{friendlyLabel}</span>
          ) : (
            <>
              <span
                className={
                  toolCall.status === 'error' && !isRecoverable
                    ? 'text-red-400/80'
                    : 'text-white/55'
                }
              >
                {verb}
              </span>
              {subject && <span className="ml-1.5 font-mono text-white/35">{subject}</span>}
            </>
          )}
          {isRecoverable && recoverableLabel && (
            <span className="ml-1.5 italic text-amber-300/50">{recoverableLabel}</span>
          )}
        </span>
        {hasExpandable && (
          <svg
            className="ml-1 shrink-0 text-white/20"
            style={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s',
            }}
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            <path
              d="M2 1.5l3 2.5-3 2.5"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Button>
      {open && toolCall.output && (
        <pre className="ml-5 mt-0.5 max-h-24 cursor-text select-text overflow-auto whitespace-pre-wrap text-xs text-white/30">
          {toolCall.output.slice(0, 500)}
          {toolCall.output.length > 500 ? '…' : ''}
        </pre>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function ToolCallCard({
  toolCall,
  newUi,
}: {
  toolCall: ToolCallEntry;
  newUi?: boolean;
}): React.JSX.Element {
  // Issue #529 \u2014 short-circuit to the subdued `RoutedHintChip` when
  // the chat reducer has tagged this tool call's `policyKind` as
  // `'routed'`. The chip carries the gate's deny prose verbatim
  // (already on `toolCall.output` from the SDK's synthesised
  // tool_result for the denied call). When `policyKind` is absent
  // OR `'blocked'`, the existing red-chip / amber-recoverable paths
  // below apply \u2014 no regression on un-classified deny sites.
  if (toolCall.policyKind === 'routed') {
    return <RoutedHintChip toolCall={toolCall} />;
  }
  // Issue #619 \u2014 pedagogical tool-result errors (Read file-too-large,
  // Glob too-many-results, Bash "Did you mean"). These carry recovery
  // copy the agent can act on; render as a subdued info chip rather
  // than the alarming red \u274C chip. Checked BEFORE the amber-recoverable
  // path so pedagogical errors get the dedicated chip rather than the
  // amber \u25E6 treatment.
  if (isPedagogicalToolError(toolCall)) {
    return <PedagogicalToolErrorChip toolCall={toolCall} />;
  }
  if (newUi) {
    return <NewUiToolCallRow toolCall={toolCall} />;
  }
  // Issue #200 / #216: detect tool errors the agent self-recovers
  // from on the very next turn. Render them as a soft amber \u25CB with
  // a short label, instead of the panic-inducing red \u2717 + raw
  // `<tool_use_error>` blob. Real failures (file not found,
  // permission denied, etc.) fall through and stay red so the user
  // can see what actually went wrong.
  const isRecoverable = isRecoverableToolError(toolCall);
  const recoverableLabel = isRecoverable ? recoverableToolErrorLabel(toolCall) : null;

  const statusColors: Record<ToolCallEntry['status'], string> = {
    running: 'text-blue-400',
    success: 'text-green-400',
    error: isRecoverable ? 'text-amber-300/70' : 'text-red-400',
  };
  // Crucible #787 (motion bundle item 1): in-flight tool-call rows
  // show a rotating spinner glyph instead of the static play
  // triangle so a glancer sees "this is actually running" without
  // leaning in. Done / error stays static. The `tool-call-spinner`
  // class is keyframe-driven in `globals.css`; reduced-motion drops
  // the rotation but keeps the glyph so the user still gets a non-
  // checkmark / non-cross signal.
  const statusIcons: Record<ToolCallEntry['status'], string> = {
    running: '\u25E0', // top-half circle, reads as a spinner under rotation
    success: '\u2713', // check
    // Hollow circle reads as "soft / non-fatal" so the user's eye
    // doesn't snap to it the way it would on a red \u2717.
    error: isRecoverable ? '\u25CB' : '\u2717',
  };

  // Format tool input for display
  const inputSummary =
    toolCall.name === 'write_file'
      ? ((toolCall.input.path as string) ?? '')
      : toolCall.name === 'read_file'
        ? ((toolCall.input.path as string) ?? '')
        : toolCall.name === 'list_files'
          ? ((toolCall.input.path as string) ?? '')
          : toolCall.name === 'run_command'
            ? ((toolCall.input.command as string) ?? '')
            : JSON.stringify(toolCall.input).slice(0, 60);

  const friendlyLabel = buildToolCallFriendlyLabel(toolCall);

  return (
    <div
      className="rounded-lg border border-white/10 bg-black/20 px-3 py-2"
      // The `tool-call-noop` testid is kept (rather than renamed to
      // `tool-call-recoverable`) so existing snapshot tests pinning
      // the #200 path keep working. New shapes added in #216 use the
      // same shell \u2014 the testid still means "this is a soft amber
      // recoverable-error card, not a real failure".
      data-testid={isRecoverable ? 'tool-call-noop' : undefined}
    >
      <div className="flex items-center gap-2 text-xs">
        <span
          className={`${statusColors[toolCall.status]}${toolCall.status === 'running' ? ' tool-call-spinner' : ''}`}
          data-testid="tool-call-status-icon"
          data-tool-call-status={toolCall.status}
        >
          {statusIcons[toolCall.status]}
        </span>
        <span className="font-mono font-medium text-white/60">{toolCall.name}</span>
        <span className="truncate text-white/30">{inputSummary}</span>
      </div>
      {isRecoverable && recoverableLabel && (
        <p className="mt-1 text-xs italic text-amber-200/60">{recoverableLabel}</p>
      )}
      {!isRecoverable && friendlyLabel && toolCall.status !== 'running' && (
        <p className="mt-1 text-xs italic text-white/50" data-testid="tool-call-friendly-label">
          {friendlyLabel}
        </p>
      )}
      {!isRecoverable && !friendlyLabel && toolCall.output && toolCall.status !== 'running' && (
        // Issue #328: tool-call output is the load-bearing copy target
        // (paths, error blobs, command output). Explicit `select-text`
        // keeps it copyable even if a future parent reintroduces
        // `select-none`. `cursor-text` advertises the affordance — kept
        // consistent with the message-content shim and the error card
        // (Codex flagged the inconsistency on PR #341).
        <pre
          className="mt-1 max-h-24 cursor-text select-text overflow-auto whitespace-pre-wrap text-xs text-white/40"
          data-testid="chat-message-tool-result"
        >
          {toolCall.output.slice(0, 500)}
          {toolCall.output.length > 500 ? '...' : ''}
        </pre>
      )}
    </div>
  );
}

/**
 * Schema cap for `AskUserQuestion` from the Claude Agent SDK
 * (`questions: { maxItems: 4 }`). The SKILL is supposed to clamp
 * to this, but we've seen cases (alpha.74 quick-batch walkthrough,
 * issue #551) where 5+ questions reach the renderer. When that
 * happens we render a structured `SchemaErrorCard` instead of
 * crashing inside `questions.map(...)` with the minified `H.map`
 * error users can't interpret, and auto-rebound the gate Promise
 * so the agent sees a structured rejection rather than a hung
 * tool call.
 */
export const ASK_USER_QUESTION_MAX = 4;

/**
 * Sentinel key on the `answers` payload the renderer uses to tell
 * the agent that an `AskUserQuestion` tool call was structurally
 * invalid and could not be rendered. We piggy-back the existing
 * `agent:answer-user-question` IPC rather than introducing a new
 * channel (issue #551); since #3158 the main-side gate translates a
 * sentinel-carrying answer into a structured `behavior: 'deny'` so
 * the crafted message reaches the model (resolving it as `allow`
 * used to crash the bundled CLI when `questions` was invalid). The
 * model treats the message as structured feedback ("this tool call
 * was rejected by the client because of schema-violation: …") and
 * pivots, typically by splitting into multiple AskUserQuestion
 * calls.
 *
 * Definition now lives with the gates' shared answer-shaping helper
 * (`@volley/crucible/runtime/ask-user-question/answer-permission-result.js`);
 * re-exported here so existing renderer consumers keep their import.
 */
export { ASK_USER_QUESTION_SCHEMA_ERROR_KEY };

/**
 * Shape of what `parseQuestions` returns. Either a successful parse
 * with the validated array, or a schema-violation result the card
 * surfaces via `SchemaErrorCard`. Returned tagged-union rather than
 * `ParsedQuestion[]` + side-channel so the renderer can pattern-
 * match without re-deriving the violation reason.
 */
type ParseQuestionsResult =
  | { kind: 'ok'; questions: ParsedQuestion[] }
  | { kind: 'empty' }
  | { kind: 'schema-violation'; reason: string; rawCount: number };

/**
 * Renders an `AskUserQuestion` tool-call as an interactive form
 * instead of the JSON-blob generic ToolCallCard. User clicks an
 * option button → its label is dispatched as the next user message,
 * which the model picks up on its next turn.
 *
 * MVP: the answer round-trips as a plain chat message (the model
 * has the question's full context from its own AskUserQuestion call,
 * so it understands the answer). Plan #2 Phase 4 will replace this
 * with the SDK's programmatic `customPermissionHandler` answer path
 * (`{ behavior: 'allow', updatedInput: { answers: {...} } }`) so the
 * answer goes directly into the SDK's structured-answer slot rather
 * than as free-form text.
 *
 * Shape (matches `AskUserQuestionInput` from the Claude Agent SDK):
 *   questions: [{
 *     question: string;          // the full prompt text
 *     header?: string;           // short chip label
 *     options: [{ label, description, preview? }, ...];
 *   }, ...]
 *
 * Defensive against the model under-populating the input — the agent
 * sometimes emits AskUserQuestion with empty `options`. In that case
 * we fall back to a plain "type your answer" textarea so the user
 * always has *some* way to respond.
 *
 * Defensive against schema violations (#551) — if the SDK input
 * carries more than `ASK_USER_QUESTION_MAX` questions (or has a
 * fundamentally broken shape), render a `SchemaErrorCard` and
 * rebound the gate Promise with a structured rejection rather
 * than letting the `.map()` blow up with a minified runtime
 * error.
 */
export function AskUserQuestionCard({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  const client = useCrucibleClient();
  // Track WHAT was answered, not just WHETHER, so we can render a
  // summary instead of disabled-looking buttons (user feedback
  // 2026-05-01: greyed-out buttons read as "broken UI").
  const [answersByIndex, setAnswersByIndex] = useState<Record<number, string>>({});
  const [submittedAnswers, setSubmittedAnswers] = useState<Record<string, string> | null>(null);
  const [freeTextByIndex, setFreeTextByIndex] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Pull the question shape out of the SDK input. Defensive parse —
  // the field is `unknown` at this layer so a malformed payload
  // can't blow up the chat. Returns a tagged-union so we can
  // distinguish "nothing parseable" (fall through to JSON card)
  // from "schema violation" (render structured error + auto-reject
  // the gate) — see #551.
  const parsed = parseQuestions(toolCall.input);

  if (parsed.kind === 'schema-violation') {
    // #551 — render a structured error card AND auto-rebound the
    // gate Promise so the agent receives a useful diagnostic
    // instead of timing out / hanging on an un-renderable tool call.
    //
    // #936 — pass the original tool input through so the card can
    // fire a `diagnostics:capture-renderer-issue` IPC and persist
    // the malformed payload under `.foundry/diagnostics/`. The
    // captured blob is what unblocks future triage: the previous
    // bug-report pipeline only had stack traces from minified
    // bundles, which lose the actual payload shape.
    return (
      <SchemaErrorCard
        toolUseID={toolCall.id}
        reason={parsed.reason}
        rawCount={parsed.rawCount}
        payload={toolCall.input}
      />
    );
  }

  if (parsed.kind === 'empty') {
    // Couldn't parse anything — fall through to the generic card so
    // the user at least sees the JSON. This is the rare-but-possible
    // case where the model emits a totally empty input.
    return <ToolCallCard toolCall={toolCall} />;
  }

  const questions = parsed.questions;

  const updateFreeText = (questionIndex: number, value: string): void => {
    setFreeTextByIndex((prev) => ({ ...prev, [questionIndex]: value }));
  };

  /**
   * Stages an answer and submits only after every question has a value.
   *
   * Args:
   *   questionIndex: Zero-based row being edited.
   *   answer: Candidate answer text from an option or text input.
   *   source: Whether the answer came from a preset option or custom text.
   *
   * Returns:
   *   Nothing.
   *
   * Raises:
   *   None.
   */
  const sendAnswer = (questionIndex: number, answer: string, source: 'preset' | 'custom'): void => {
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer || submittedAnswers || submitting) return;

    const nextAnswersByIndex = { ...answersByIndex, [questionIndex]: trimmedAnswer };
    setAnswersByIndex(nextAnswersByIndex);
    if (source === 'preset') {
      // Preset clicks supersede any draft custom text for that row.
      setFreeTextByIndex((prev) => ({ ...prev, [questionIndex]: '' }));
    }

    if (!questions.every((_, index) => nextAnswersByIndex[index]?.trim())) return;

    // Use the SDK-native gate path. `agent:answer-user-question`
    // resolves the held-open `canUseTool` Promise with `{ behavior:
    // 'allow', updatedInput: { answers } }`, the SDK then feeds the
    // tool's completion (carrying the answer) back into the model
    // and continues the turn. Verified working in SDK 0.2.126.
    //
    // **Why we removed the chat-message fallback** (the legacy
    // `foundry:ask-user-question:answer` CustomEvent path):
    //
    //   The original card from PR #129 fired BOTH the IPC AND a
    //   CustomEvent that the workspace listener turned into a
    //   regular `agent:send`. That was belt-and-braces when the
    //   IPC gate path was newly wired and its semantics murky.
    //
    //   With both paths firing, the SDK's gate resolution
    //   continues the existing turn (`session.inFlight = true`),
    //   and milliseconds later the chat-message dispatch hits
    //   `agent:send` — which bounces with the in-flight bouncer
    //   error. User-reported on alpha.42 mid-build-game flow:
    //   click an option, immediately see the red "Another message
    //   is already in flight" bubble.
    //
    //   Drop the dispatch. If the gate path EVER fails to deliver
    //   the answer (it doesn't, currently), that's a fixable bug
    //   in agent-permissions / the SDK gate — not something to
    //   paper over with a misrouted chat message.
    const answers = questions.reduce<Record<string, string>>((acc, question, index) => {
      acc[question.question] = nextAnswersByIndex[index];
      return acc;
    }, {});
    setSubmitting(true);
    setSubmissionError(null);
    void client
      .invoke('agent:answer-user-question', { toolUseID: toolCall.id, answers })
      .then((result) => {
        if (result.ok) {
          setSubmittedAnswers(answers);
          return;
        }
        setSubmissionError(`Foundry did not accept this answer (${result.reason}).`);
      })
      .catch(() => {
        console.error('[chat] agent:answer-user-question IPC failed');
        setSubmissionError('Foundry could not submit this answer.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // After the user answers, replace the entire form with a clean
  // summary card showing what was picked. Avoids the "ghosted
  // disabled controls" UX that read as broken (user report
  // 2026-05-01: "everything's disabled out"). When the SDK then
  // emits its canonical tool_call chunk and we upsert the input
  // (e.g. previously-empty options become populated), the card
  // STAYS in summary mode rather than reverting to a fresh-form
  // appearance — the user already gave their answer.
  if (submittedAnswers) {
    const submittedEntries = Object.entries(submittedAnswers);
    return (
      <div className="rounded-lg border border-[var(--color-success-border)] bg-[var(--color-success-subtle)] px-3 py-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 text-emerald-300/80">✓</span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-white/40">You answered</p>
            <div className="mt-1 space-y-2">
              {submittedEntries.map(([question, answer]) => (
                <div key={question}>
                  <p className="text-sm font-medium text-white/90">{answer}</p>
                  <p className="mt-0.5 text-xs text-white/40 italic">
                    re: <span className="not-italic">{question}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3">
      {submissionError && (
        <p
          className="mb-3 rounded border border-red-400/30 bg-red-500/10 px-2 py-1.5 text-xs text-red-200"
          role="alert"
        >
          {submissionError} Your selections are still here; try again.
        </p>
      )}
      {submitting && <p className="mb-3 text-xs text-white/50">Submitting…</p>}
      {questions.map((q, qi) => {
        const rowAnswer = answersByIndex[qi];
        const rowFreeText = freeTextByIndex[qi] ?? '';
        return (
          <div key={qi} className={qi === 0 ? '' : 'mt-4 border-t border-white/10 pt-4'}>
            {q.header && (
              <span className="mb-1 inline-block rounded-full bg-crucible-accent/20 px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-crucible-accent-hover">
                {q.header}
              </span>
            )}
            <p className="mb-2 text-sm font-medium text-white/90">{q.question}</p>
            {rowAnswer && (
              <p className="mb-2 rounded border border-[var(--color-success-border)] bg-[var(--color-success-subtle)] px-2 py-1 text-xs text-white/50">
                Current answer: <span className="font-medium text-emerald-100/90">{rowAnswer}</span>
              </p>
            )}
            {q.options.length > 0 ? (
              <div className="grid gap-1.5">
                {q.options.map((opt, oi) => {
                  const isSelected = rowAnswer === opt.label;
                  return (
                    <Button
                      key={oi}
                      type="button"
                      variant="secondary"
                      aria-pressed={isSelected}
                      onClick={() => sendAnswer(qi, opt.label, 'preset')}
                      disabled={submitting}
                      className={`group flex flex-col items-start rounded-lg h-auto text-left px-3 py-2 transition-colors ${
                        isSelected
                          ? 'border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/15'
                          : 'border-white/10 bg-black/20 hover:border-crucible-accent/40 hover:bg-crucible-accent/10'
                      }`}
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="text-sm font-medium text-white/90 group-hover:text-white">
                          {opt.label}
                        </span>
                        {isSelected && (
                          <span
                            aria-hidden="true"
                            className="text-[0.625rem] font-medium uppercase tracking-wide text-emerald-200/80"
                          >
                            Selected
                          </span>
                        )}
                      </span>
                      {opt.description && (
                        <span className="mt-0.5 text-xs text-white/50">{opt.description}</span>
                      )}
                    </Button>
                  );
                })}
                {/* "Other" affordance — the SDK prompt says we should
                  always provide one. */}
                <details className="rounded-lg border border-white/10 bg-black/10 px-3 py-2">
                  <summary className="cursor-pointer text-xs text-white/40 hover:text-white/60">
                    None of these — type my own answer
                  </summary>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={rowFreeText}
                      onChange={(e) => updateFreeText(qi, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') sendAnswer(qi, rowFreeText, 'custom');
                      }}
                      placeholder="Type your answer..."
                      className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs focus:border-crucible-accent focus:outline-none"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => sendAnswer(qi, rowFreeText, 'custom')}
                      disabled={!rowFreeText.trim() || submitting}
                    >
                      Send
                    </Button>
                  </div>
                </details>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={rowFreeText}
                  onChange={(e) => updateFreeText(qi, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') sendAnswer(qi, rowFreeText, 'custom');
                  }}
                  placeholder="Type your answer..."
                  className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-sm focus:border-crucible-accent focus:outline-none"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => sendAnswer(qi, rowFreeText, 'custom')}
                  disabled={!rowFreeText.trim() || submitting}
                >
                  Send
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Parsed shape of a question we render. Matches the SDK type but
 * loosened (all fields optional) so a partially-populated input
 * still produces something sensible.
 */
interface ParsedQuestion {
  question: string;
  header?: string;
  options: { label: string; description?: string; preview?: string }[];
}

export function parseQuestions(input: Record<string, unknown>): ParseQuestionsResult {
  const raw = (input as { questions?: unknown })?.questions;
  // Missing/non-array `questions` is a hard schema violation —
  // there's nothing for us to render and the SDK contract says
  // `questions` is required + must be an array. Surface it as a
  // structured error so the agent gets a useful diagnostic
  // rather than the renderer silently falling back to the JSON
  // dump (which the user can't act on either).
  if (!Array.isArray(raw)) {
    return {
      kind: 'schema-violation',
      reason: 'missing-or-invalid-questions',
      rawCount: 0,
    };
  }
  // Schema cap: `questions: { maxItems: 4 }`. Trip BEFORE the
  // per-item parse loop so the user / agent gets the cap message
  // even when the over-cap items are individually well-formed.
  if (raw.length > ASK_USER_QUESTION_MAX) {
    return {
      kind: 'schema-violation',
      reason: 'too-many-questions',
      rawCount: raw.length,
    };
  }
  const out: ParsedQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== 'object') continue;
    const qq = q as Record<string, unknown>;
    const question = typeof qq.question === 'string' ? qq.question : '';
    if (!question) continue;
    const header = typeof qq.header === 'string' ? qq.header : undefined;
    const optionsRaw = qq.options;
    const options: ParsedQuestion['options'] = [];
    if (Array.isArray(optionsRaw)) {
      for (const o of optionsRaw) {
        if (!o || typeof o !== 'object') continue;
        const oo = o as Record<string, unknown>;
        const label = typeof oo.label === 'string' ? oo.label : undefined;
        if (!label) continue;
        options.push({
          label,
          description: typeof oo.description === 'string' ? oo.description : undefined,
          preview: typeof oo.preview === 'string' ? oo.preview : undefined,
        });
      }
    }
    out.push({ question, header, options });
  }
  // Post-parse: if every item was filtered out (no `question`
  // strings survived) treat it as empty rather than a violation
  // — the existing JSON-card fallback handles that case fine and
  // we don't want to spam the agent with a false-positive
  // rejection for a fundamentally empty input.
  if (out.length === 0) return { kind: 'empty' };
  return { kind: 'ok', questions: out };
}

/**
 * Decision for what a composer send should do while an `AskUserQuestion`
 * gate is open — the "Waiting on your input…" contradiction fix.
 *
 * The bug: the status row shows "Waiting on your input…" off the run-state
 * (`deriveChatStatusRow` → `waiting-on-user`), but the composer's
 * `user/send` queues purely on `isAiTyping` (`chat/state.ts`). During a gate
 * the turn is paused mid-tool, so `isAiTyping` stays true → the user's typed
 * answer lands in the QUEUED chip and never reaches the paused agent.
 *
 * The fix routes a typed reply to the open gate's answer — the SAME path
 * `AskUserQuestionCard` uses (`agent:answer-user-question` IPC), which RESUMES
 * the paused turn rather than starting a new one, so it does NOT bounce off
 * the in-flight turn (the #170 / alpha.42 "Another message is already in
 * flight" failure that killed the old chat-message fallback).
 *
 * Scope: a SINGLE pending question — the unambiguous clarifying-gate shape a
 * free-text composer reply can actually answer (and the case users hit). A
 * multi-question gate keeps the structured card as its answer surface, so we
 * fall through to the normal send/queue path there.
 */
export type ComposerSendDecision =
  | { readonly kind: 'send' }
  | {
      readonly kind: 'answer-question';
      readonly toolUseID: string;
      readonly answers: Record<string, string>;
    };

export function decideComposerSend(args: {
  readonly pinnedAskUserQuestion: ToolCallEntry | null;
  readonly text: string;
}): ComposerSendDecision {
  const text = args.text.trim();
  const pinned = args.pinnedAskUserQuestion;
  if (!text || !pinned) return { kind: 'send' };
  const parsed = parseQuestions(pinned.input ?? {});
  // Only route the free-text reply when there is exactly ONE question to
  // answer; anything else (no questions, schema violation, multi-question
  // card) keeps the existing send path + structured card.
  if (parsed.kind !== 'ok' || parsed.questions.length !== 1) return { kind: 'send' };
  return {
    kind: 'answer-question',
    toolUseID: pinned.id,
    answers: { [parsed.questions[0].question]: text },
  };
}

/**
 * #3060 — user-facing message for a FAILED composer gate answer. The composer's
 * answer-the-pinned-question path (see decideComposerSend) fires
 * agent:answer-user-question and, on the cloud path, that invoke can reject
 * (WSS drop / pod roll) or resolve `{ ok: false }`. The typed text is preserved
 * (the composer clears only after ok), but the user needs a VISIBLE signal that
 * delivery failed and the gate is still open — otherwise the answer silently
 * sits in the box against a still-blocked build. Mirrors the card's own
 * submissionError copy (AskUserQuestionCard).
 */
export function composerAnswerErrorMessage(
  outcome: { readonly kind: 'rejected'; readonly reason: string } | { readonly kind: 'threw' },
): string {
  if (outcome.kind === 'rejected') {
    return `Couldn't submit your answer (${outcome.reason}). Your text is kept — press send to try again.`;
  }
  return `Couldn't reach Foundry to submit your answer. Your text is kept — press send to try again.`;
}

/**
 * #551 — error card the renderer shows when an `AskUserQuestion`
 * tool call is structurally invalid (too many questions, missing
 * `questions` array, etc). Two responsibilities:
 *
 *   1. Render a user-facing explanation of what went wrong so the
 *      chat doesn't just blank out / crash with a `H.map` minified
 *      error.
 *   2. Auto-rebound the SDK's gate Promise by invoking
 *      `agent:answer-user-question` with the
 *      `ASK_USER_QUESTION_SCHEMA_ERROR_KEY` sentinel. The agent
 *      receives this as the answer payload and pivots (typically to
 *      two split sub-4 AskUserQuestion calls).
 *
 * The IPC fire is wrapped in a one-shot `useEffect` keyed on
 * `toolUseID` so a re-render (e.g. parent message-list update)
 * doesn't double-fire. The gate's `resolveAnswer` is idempotent on
 * stale IDs but we'd rather not flood it.
 */
export function SchemaErrorCard({
  toolUseID,
  reason,
  rawCount,
  payload,
}: {
  toolUseID: string;
  reason: string;
  rawCount: number;
  /** Issue #936 — the original `toolCall.input` that failed the
   *  schema check. Forwarded to `diagnostics:capture-renderer-issue`
   *  so we can repro from real malformed payloads instead of
   *  guessing at minified runtime errors. Optional so existing
   *  tests / callers without the new field keep working — the
   *  capture useEffect simply skips when payload is undefined. */
  payload?: unknown;
}): React.JSX.Element {
  const client = useCrucibleClient();
  // Fire the gate-rebound IPC exactly once per toolUseID. Stuffing
  // this in a useEffect keeps the render pure and lets vitest's
  // `node` environment evaluate the SSR output without crashing —
  // useEffect doesn't fire under `renderToStaticMarkup`, so the
  // unit test asserts the IPC contract by invoking the effect's
  // payload directly (see exported `buildSchemaErrorAnswers`).
  useEffect(() => {
    const answers = buildSchemaErrorAnswers(reason, rawCount);
    void client.invoke('agent:answer-user-question', { toolUseID, answers }).catch(() => {
      // Same log-only convention as the happy-path sendAnswer in
      // AskUserQuestionCard. The user already sees the schema
      // error card; a flaky IPC shouldn't promote that to a UI
      // failure too.
      console.error('[chat] agent:answer-user-question rejection IPC failed');
    });
  }, [toolUseID, reason, rawCount]);

  // #936 — fire-and-forget capture of the malformed `AskUserQuestion`
  // input. `gameId` comes via context from `<ChatErrorBoundary>`'s
  // surface provider; without it (a future render path mounting the
  // card outside the boundary) we skip the capture rather than
  // pollute a global "no-game" diagnostics directory. Best-effort —
  // a failed IPC stays log-only, the gate-rebound above is the
  // load-bearing recovery path.
  const { gameId: surfaceGameId } = useContext(ChatSurfaceContext);
  useEffect(() => {
    if (!surfaceGameId || payload === undefined) return;
    void client
      .invoke('diagnostics:capture-renderer-issue', {
        gameId: surfaceGameId,
        kind: 'malformed-askq',
        message: reason,
        payload,
        toolUseID,
      })
      .catch(() => {
        // Log-only — same convention as the gate-rebound above.
        console.error('[chat] diagnostics:capture-renderer-issue failed');
      });
  }, [surfaceGameId, toolUseID, reason, payload]);

  const userMessage =
    reason === 'too-many-questions'
      ? `Foundry couldn't render this AskUserQuestion: ${rawCount} questions were requested but the SDK schema caps the call at ${ASK_USER_QUESTION_MAX}. The agent has been told to retry by splitting into multiple smaller AskUserQuestion calls.`
      : `Foundry couldn't render this AskUserQuestion: the payload was missing a valid \`questions\` array. The agent has been told to retry.`;

  return (
    <div
      role="alert"
      className="rounded-lg border border-[var(--color-warning-border)] bg-[var(--color-warning-subtle)] px-3 py-2"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-amber-300/80">!</span>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-amber-200/80">
            Tool call rejected — schema violation
          </p>
          <p className="mt-0.5 text-sm text-white/80">{userMessage}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Build the `answers` payload the `SchemaErrorCard` rebounds to
 * the SDK gate. Exported separately so the unit test can pin the
 * exact shape the agent receives without having to mock React's
 * effect scheduler — the message text is part of the rejection
 * contract.
 */
export function buildSchemaErrorAnswers(reason: string, rawCount: number): Record<string, string> {
  // Recovery hint copy — crucible#3383 removed the `BulkInterview` MCP
  // tool entirely (no live UI ever routed a game to it), so the only
  // recovery path left is splitting into multiple AskUserQuestion calls.
  const message =
    reason === 'too-many-questions'
      ? `AskUserQuestion received ${rawCount} questions; the SDK schema caps this at ${ASK_USER_QUESTION_MAX} (maxItems). Foundry rejected the call. Retry by splitting into multiple AskUserQuestion calls of at most ${ASK_USER_QUESTION_MAX} questions each.`
      : `AskUserQuestion was missing a valid \`questions\` array. Foundry rejected the call. Retry with a payload shaped { questions: [{ question, header?, options: [{ label, description?, preview? }] }, ...] } where questions.length is between 1 and ${ASK_USER_QUESTION_MAX}.`;
  return {
    [ASK_USER_QUESTION_SCHEMA_ERROR_KEY]: JSON.stringify({
      ok: false,
      reason: 'schema-violation',
      detail: reason,
      max: ASK_USER_QUESTION_MAX,
      received: rawCount,
      message,
    }),
  };
}

/**
 * Render message content as Markdown — paragraphs, bold, italic,
 * unordered lists, fenced code blocks, inline code. We hand-roll a
 * tiny renderer rather than pulling in `react-markdown` because the
 * surface is small and the dep chain (remark + rehype + plugins)
 * would be 100x the size of the actual logic.
 *
 * What's supported (deliberately narrow):
 *   - Triple-backtick code fences with optional language tag
 *   - `inline code`
 *   - `**bold**` and `*italic*` (italic uses single asterisk and
 *     skips when at the start of a line so list bullets aren't
 *     mis-parsed)
 *   - `- item` / `* item` unordered lists at the start of a line
 *   - Blank lines split paragraphs
 *
 * What's NOT supported:
 *   - Headings, tables, links, images, blockquotes, ordered lists,
 *     nested lists. The agent rarely emits these in chat and the
 *     CommonMark complexity isn't worth it.
 */
export function MessageContent({ content }: { content: string }): React.JSX.Element {
  // First split on triple-backtick code fences — they're the only
  // multi-line construct we care about that nests around other
  // formatting.
  const parts = content.split(/(```[\s\S]*?```)/g);

  // `overflow-wrap: anywhere` (inherited, so the inline <code> chips pick it
  // up) breaks long unbreakable tokens — file paths, URLs, IDs — that would
  // otherwise push the message past the panel width and make the chat scroll
  // horizontally. `min-w-0` lets this block shrink inside its flex ancestors.
  // Fenced ``` blocks keep their own overflow-x-auto (white-space: pre — they
  // scroll internally rather than wrap).
  return (
    <div className="min-w-0 [overflow-wrap:anywhere]">
      {parts.map((part, i) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          // Strip optional language tag on the first line.
          const inner = part.slice(3, -3).replace(/^[\w-]+\n/, '');
          return (
            <pre
              key={i}
              className="my-3 min-w-0 overflow-x-auto rounded-xl p-4 font-mono text-[13px] leading-relaxed"
              style={{ background: 'var(--bg-elev-3)', color: 'var(--text-2)' }}
            >
              <code>{inner}</code>
            </pre>
          );
        }
        return <MarkdownProse key={i} text={part} />;
      })}
    </div>
  );
}

/**
 * Render a non-code-fence segment of markdown text. Splits into
 * paragraphs on blank lines, runs each paragraph through the inline
 * formatter, and renders a list when a paragraph is entirely
 * `- item` lines.
 */
function MarkdownProse({ text }: { text: string }): React.JSX.Element {
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim().length > 0);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        // List detection: every non-blank line starts with `- ` or `* `.
        const isList = lines.every((l) => /^\s*[-*]\s+/.test(l));
        if (isList) {
          return (
            <ul key={i} className="my-4 space-y-1.5 pl-5" style={{ listStyleType: 'disc' }}>
              {lines.map((l, j) => (
                <li key={j} className="pl-1">
                  {renderInline(l.replace(/^\s*[-*]\s+/, ''))}
                </li>
              ))}
            </ul>
          );
        }
        // Ordered list detection: every non-blank line starts with `1. `, `2. `, etc.
        const isOrderedList = lines.every((l) => /^\s*\d+\.\s+/.test(l));
        if (isOrderedList) {
          return (
            <ol key={i} className="my-4 space-y-2 pl-5" style={{ listStyleType: 'decimal' }}>
              {lines.map((l, j) => (
                <li key={j} className="pl-1">
                  {renderInline(l.replace(/^\s*\d+\.\s+/, ''))}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i} className={i === 0 ? '' : 'mt-5'}>
            {renderInline(block)}
          </p>
        );
      })}
    </>
  );
}

/**
 * Match an `http://` or `https://` URL through the next whitespace
 * (or backtick / closing bracket / end of string). The trailing-
 * punctuation strip below trims things like `.,;:!?)` that are
 * almost always sentence punctuation, not part of the URL.
 */
const URL_PATTERN = /https?:\/\/[^\s`)\]]+/;

/**
 * Strip a URL's trailing punctuation that's almost certainly part
 * of the surrounding sentence (e.g. `Visit https://example.com.` —
 * the `.` ends the sentence, not the path). Conservative: only
 * strips a single trailing punctuation character from a known
 * bad-tail set so we don't break URLs that legitimately end in `/`
 * or query strings ending in `?`. Returns the trimmed URL plus
 * the consumed length so the caller knows how far to advance.
 */
function trimUrlTail(url: string): { url: string; len: number } {
  const m = /[.,;:!?)\]]+$/.exec(url);
  if (m) {
    return { url: url.slice(0, m.index), len: m.index };
  }
  return { url, len: url.length };
}

/**
 * Render an inline anchor that opens in the system browser. Relies
 * on `setWindowOpenHandler` in main/index.ts to intercept the
 * `target="_blank"` navigation and route through `shell.openExternal`
 * — that handler is already wired for the About-dialog and other
 * external links elsewhere in the app.
 */
function ChatLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? 'text-crucible-accent-hover underline-offset-2 hover:underline'}
    >
      {children}
    </a>
  );
}

/**
 * Walk a single line of text and replace bold / italic / inline-code
 * / URL runs with React nodes. Uses a tokeniser rather than chained
 * regexes so a `**word*` pattern doesn't cross-fire bold and italic.
 *
 * URL handling has two routes:
 *   - Bare URL in plain text → wrapped in a styled `<a>` link
 *   - URL inside backticks (e.g. `http://localhost:3000`) → wrapped
 *     in an `<a>` AND keeps the inline-code styling, since users
 *     often format developer-facing URLs as code in markdown.
 */
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;
  let buf = '';
  let key = 0;
  const flush = (): void => {
    if (buf) {
      // Convert single line breaks (within a paragraph) to <br>.
      const segs = buf.split('\n');
      segs.forEach((s, idx) => {
        if (idx > 0) out.push(<br key={`br-${key++}`} />);
        if (s) out.push(s);
      });
      buf = '';
    }
  };
  while (i < text.length) {
    // Inline code — highest priority, escapes everything else.
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        flush();
        const inner = text.slice(i + 1, end);
        // Linkify if the entire code-span is a URL — common for
        // developer URLs that the agent tends to wrap in backticks
        // (`http://localhost:3000` etc.).
        const codeClass =
          'rounded-md px-1.5 py-0.5 font-mono text-[13px]' + ' bg-white/[0.08] text-white/80';
        if (/^https?:\/\/\S+$/.test(inner)) {
          out.push(
            <ChatLink key={key++} href={inner} className={`${codeClass} hover:underline`}>
              {inner}
            </ChatLink>,
          );
        } else {
          out.push(
            <code key={key++} className={codeClass}>
              {inner}
            </code>,
          );
        }
        i = end + 1;
        continue;
      }
    }
    // Bold — `**...**`
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 1) {
        flush();
        out.push(<strong key={key++}>{renderInline(text.slice(i + 2, end))}</strong>);
        i = end + 2;
        continue;
      }
    }
    // Italic — `*...*`. Require non-space after opener and non-space
    // before closer so unbalanced asterisks (e.g. list bullets, `5 *
    // 3`) don't trigger.
    if (text[i] === '*' && text[i + 1] !== '*' && text[i + 1] !== ' ') {
      const end = text.indexOf('*', i + 1);
      if (end > i && text[end - 1] !== ' ') {
        flush();
        out.push(<em key={key++}>{renderInline(text.slice(i + 1, end))}</em>);
        i = end + 1;
        continue;
      }
    }
    // Bare URL — http:// or https://. Match against the slice
    // starting at the cursor so we don't have to scan the whole
    // string from the top each iteration.
    if (text[i] === 'h') {
      const m = URL_PATTERN.exec(text.slice(i));
      if (m && m.index === 0) {
        const trimmed = trimUrlTail(m[0]);
        if (trimmed.url.length > 8) {
          flush();
          out.push(
            <ChatLink key={key++} href={trimmed.url}>
              {trimmed.url}
            </ChatLink>,
          );
          i += trimmed.len;
          continue;
        }
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

export function TypingIndicator(): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:0ms]" />
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:150ms]" />
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:300ms]" />
    </div>
  );
}
